import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { LOCAL_STORAGE } from "@constants/index";
import { isAuthenticated } from "@utils/auth";

export const ClientConfigContext = createContext();

// Global reference to call from outside
let globalSetClientConfigValue = null;
export const setClientConfigValue = (key, value) => {
  if (globalSetClientConfigValue) {
    globalSetClientConfigValue(key, value);
  }
};

export const useClientConfig = () => {
  const context = useContext(ClientConfigContext);
  if (context) {
    return context;
  } else {
    return null;
  }
};

/** Y/N client-config keys mirrored to localStorage for cold-start / race fallback */
const LOCAL_STORAGE_FLAG_BY_CONFIG_KEY = {
  useImportComponent: LOCAL_STORAGE.USE_IMPORT_COMPONENT,
  usePreview: LOCAL_STORAGE.USE_PREVIEW,
  useVex: LOCAL_STORAGE.USE_VEX,
  isSkipClarityProgress: LOCAL_STORAGE.IS_SKIP_CLARITY_PROGRESS,
};

const syncClientConfigFlagsToLocalStorage = (config = {}) => {
  Object.entries(LOCAL_STORAGE_FLAG_BY_CONFIG_KEY).forEach(([configKey, storageKey]) => {
    if (config[configKey]) {
      localStorage.setItem(storageKey, config[configKey].codeValue || "N");
    }
  });
};

const readLocalStorageFlag = (configKey) => {
  const storageKey = LOCAL_STORAGE_FLAG_BY_CONFIG_KEY[configKey];
  if (!storageKey) return null;
  const value = localStorage.getItem(storageKey);
  return value === "Y" || value === "N" ? value : null;
};

const AUTO_FETCH_MAX_REQUESTS = 3;
const AUTO_FETCH_RETRY_DELAY_MS = 500;
/** Stop waiting for the auth session (Keycloak init is async) after this long */
const AUTO_FETCH_AUTH_WAIT_MS = 15000;

export const ClientConfigProvider = ({ children }) => {
  const [clientConfig, setClientConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const autoFetchRequestsRef = useRef(0);
  const autoFetchStartedAtRef = useRef(null);

  // API call to get runtime client config (feature flags / UI defaults — not System Preferences)
  const getClientConfig = useMutationData(
    queryKeys.getClientConfig,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && typeof response !== "string") {
          setClientConfig(response);
          syncClientConfigFlagsToLocalStorage(response);
          setError(null);
        }
      },
      onError: (error) => {
        console.error("Error fetching client config:", error);
        setError(error);
      },
      onSettled: () => {
        setIsLoading(false);
      },
    },
  );

  // Returns the mutation promise so login / Keycloak bootstrap can await a real load.
  // Never rejects: a failed client-config load must not break those flows (login would
  // report an error without navigating, Keycloak would clear the session).
  const fetchClientConfig = useCallback(() => {
    setIsLoading(true);
    setError(null);
    return getClientConfig.mutateAsync().catch(() => null);
  }, [getClientConfig.mutateAsync]);

  // Called when System Preferences are saved so in-memory client config stays in sync
  const updateClientConfig = (newClientConfig) => {
    setClientConfig(newClientConfig);
    syncClientConfigFlagsToLocalStorage(newClientConfig);
  };

  // Auto-fetch while empty (skip login / preferences pages to avoid duplicate calls).
  // Login / Keycloak also call fetchClientConfig() explicitly after auth is ready; this
  // effect re-runs when a request settles, so it retries when that load fails.
  // Loads GET /client-config (not /preferences — that is superuser-only for Preferences page).
  useEffect(() => {
    if (Object.keys(clientConfig).length > 0 || isLoading) {
      return;
    }
    if (autoFetchRequestsRef.current >= AUTO_FETCH_MAX_REQUESTS) {
      return;
    }

    const pathname = window?.location?.pathname || "";
    if (pathname.includes("/preferences") || pathname.includes("/login")) {
      return;
    }

    if (autoFetchStartedAtRef.current === null) {
      autoFetchStartedAtRef.current = Date.now();
    }

    let timer;
    const run = () => {
      // useMutationData rejects when there is no session yet, and Keycloak init is
      // async, so wait for the session instead of burning a request on it.
      if (!isAuthenticated()) {
        if (Date.now() - autoFetchStartedAtRef.current <= AUTO_FETCH_AUTH_WAIT_MS) {
          timer = setTimeout(run, AUTO_FETCH_RETRY_DELAY_MS);
        }
        return;
      }
      autoFetchRequestsRef.current += 1;
      fetchClientConfig();
    };

    timer = setTimeout(run, autoFetchRequestsRef.current === 0 ? 0 : AUTO_FETCH_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [fetchClientConfig, clientConfig, isLoading]);

  useEffect(() => {
    if (Object.keys(clientConfig).length > 0) {
      syncClientConfigFlagsToLocalStorage(clientConfig);
    }
  }, [clientConfig]);

  // Function to get a specific client-config value
  const getClientConfigValue = (key) => {
    return clientConfig[key]?.codeValue || null;
  };

  // Function to get a specific client-config object
  const getClientConfigItem = (key) => {
    return clientConfig[key] || null;
  };

  // Y/N flags: prefer context; fall back to localStorage when config not loaded yet
  const isClientConfigEnabled = (key) => {
    const value = getClientConfigValue(key) ?? readLocalStorageFlag(key);
    return value === "Y";
  };

  // Function to get multiple client-config items at once
  const getMultipleClientConfigItems = (keys) => {
    const result = {};
    keys.forEach((key) => {
      result[key] = getClientConfigItem(key);
    });
    return result;
  };

  // Function to get multiple client-config values at once
  const getMultipleClientConfigValues = (keys) => {
    const result = {};
    keys.forEach((key) => {
      result[key] = getClientConfigValue(key);
    });
    return result;
  };

  const getClientConfigRemark = (key) => {
    return clientConfig[key]?.remark || null;
  };

  // Function to set a single client-config value
  const setClientConfigValueInternal = (key, value) => {
    setClientConfig((prev) => ({
      ...prev,
      [key]: typeof value === 'object' && value !== null
        ? value
        : { codeValue: value }
    }));
  };

  // Set global reference khi component mount
  useEffect(() => {
    globalSetClientConfigValue = setClientConfigValueInternal;
    return () => {
      globalSetClientConfigValue = null;
    };
  }, []);

  const value = {
    clientConfig,
    isLoading,
    error,
    fetchClientConfig,
    updateClientConfig,
    getClientConfigValue,
    getClientConfigItem,
    isClientConfigEnabled,
    getMultipleClientConfigItems,
    getMultipleClientConfigValues,
    getClientConfigRemark
  };

  return <ClientConfigContext.Provider value={value}>{children}</ClientConfigContext.Provider>;
};
